import { useMemo, useState } from "react";
import { AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useUnlinkedSpareParts, useLinkUnlinkedPart } from "@/hooks/useSparePartTypes";

const db = supabase as any;

function PickerCell({ sparePartId, applianceCategory }: { sparePartId: string; applianceCategory?: string | null }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [showAll, setShowAll] = useState(false);
  const link = useLinkUnlinkedPart();

  const { data: types = [] } = useQuery({
    queryKey: ["spare_part_types_all"],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await db
        .from("spare_part_types")
        .select("id, name, parts_category_id, harvest_components:parts_category_id(name, appliance_category)")
        .order("name")
        .limit(500);
      if (error) throw error;
      return data || [];
    },
  });

  const cat = applianceCategory?.trim().toLowerCase() || "";

  const scoped = useMemo(() => {
    if (!cat || showAll) return types;
    return types.filter((t: any) => {
      const tc = t.harvest_components?.appliance_category?.trim().toLowerCase();
      return !tc || tc === cat;
    });
  }, [types, cat, showAll]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return scoped.slice(0, 30);
    return scoped
      .filter((t: any) =>
        t.name.toLowerCase().includes(needle) ||
        t.harvest_components?.name?.toLowerCase().includes(needle) ||
        t.harvest_components?.appliance_category?.toLowerCase().includes(needle))
      .slice(0, 30);
  }, [scoped, q]);


  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-7 text-xs">Link to type…</Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="end">
        <Input placeholder="Search part types…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 text-sm mb-2" />
        {cat && (
          <div className="flex items-center justify-between text-[11px] text-muted-foreground px-1 mb-1">
            <span>Filtered to: <span className="font-medium text-foreground">{applianceCategory}</span></span>
            <button type="button" onClick={() => setShowAll((v) => !v)} className="underline hover:text-foreground">
              {showAll ? "Filter" : "Show all"}
            </button>
          </div>
        )}
        <div className="max-h-64 overflow-y-auto space-y-1">
          {filtered.map((t: any) => (
            <button
              key={t.id}
              type="button"
              onClick={async () => {
                await link.mutateAsync({ sparePartId, partTypeId: t.id });
                setOpen(false);
              }}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-muted text-xs"
            >
              <div className="font-medium">{t.name}</div>
              <div className="text-muted-foreground">
                {t.harvest_components?.appliance_category || "Generic"} › {t.harvest_components?.name}
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="text-xs text-muted-foreground p-2">No matches.</div>
          )}
        </div>
      </PopoverContent>

    </Popover>
  );
}

export default function UnlinkedSparePartsPanel() {
  const { data: parts = [], isLoading } = useUnlinkedSpareParts();

  return (
    <section className="px-6 mt-6">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle className="w-5 h-5 text-amber-600" />
        <h2 className="text-lg font-semibold">Unlinked Spare Parts</h2>
        <Badge variant="secondary">{parts.length}</Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-3">
        Actual harvested parts not yet linked to a canonical Part Type. Link them so the same component groups together across sources.
      </p>
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>OEM #</TableHead>
              <TableHead>Appliance Type</TableHead>
              <TableHead className="w-[140px] text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">Loading…</TableCell></TableRow>
            ) : parts.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-6">All spare parts are linked. 🎉</TableCell></TableRow>
            ) : parts.map((p: any) => (
              <TableRow key={p.id}>
                <TableCell className="font-medium text-sm">{p.name}</TableCell>
                <TableCell className="text-sm">{p.brand || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.model_number || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.part_number || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{p.appliances?.category || "—"}</TableCell>
                <TableCell className="text-right"><PickerCell sparePartId={p.id} applianceCategory={p.appliances?.category} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
